// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminTransactions\AdminTransactions.js
// Версия: 3.4.0 - Full details table
// Дата: 19.12.2025

import React, { useState, useEffect } from 'react';
import './AdminTransactions.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleting, setDeleting] = useState(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => { fetchTransactions(); }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL + '/api/admin/transactions?limit=200', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      const data = await response.json();
      const txList = data.data || data.transactions || [];
      setTransactions(Array.isArray(txList) ? txList : []);
      calculateStats(txList);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (txList) => {
    const completed = txList.filter(t => t.status === 'completed' || t.status === 'success');
    setStats({
      total: txList.length,
      completed: completed.length,
      totalVolume: completed.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить #' + id + '?')) return;
    try {
      setDeleting(id);
      await fetch(API_URL + '/api/admin/transactions/' + id, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return [tx.id?.toString(), tx.from_user_email, tx.to_user_email, tx.user_email, tx.description]
      .filter(Boolean).join(' ').toLowerCase().includes(query);
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const getTypeInfo = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'voucher_claim' || t === 'qr_claim') return { icon: '🎉', label: 'Получение', color: 'green' };
    if (t === 'voucher_create' || t === 'qr_create') return { icon: '🎁', label: 'Создание', color: 'blue' };
    if (t === 'admin_topup') return { icon: '👑', label: 'Бонус', color: 'purple' };
    if (t === 'topup' || t === 'deposit') return { icon: '💰', label: 'Пополнение', color: 'teal' };
    if (t === 'spend' || t === 'withdraw') return { icon: '💸', label: 'Списание', color: 'red' };
    if (t === 'transfer') return { icon: '↔️', label: 'Перевод', color: 'orange' };
    return { icon: '📄', label: type || '?', color: 'gray' };
  };

  const shortEmail = (email) => email ? email.split('@')[0] : '-';

  const getGiftType = (tx) => {
    const cat = tx.gift_category || '';
    const desc = (tx.description || '').toLowerCase();
    
    // Маппинг категорий на эмодзи
    const categories = {
      'gift': '🎁 Подарок',
      'travel': '✈️ Путешествие',
      'birthday': '🎂 День рождения',
      'gaming': '🎮 Игры',
      'coffee': '☕ Кофе',
      'food': '🍕 Еда',
      'thank': '🙏 Спасибо',
      'corporate': '🏢 Корпоратив',
      'love': '❤️ Любовь',
      'sport': '⚽ Спорт'
    };
    
    if (categories[cat]) return categories[cat];
    if (desc.includes('promot') || desc.includes('credit')) return '⭐ Промо';
    if (desc.includes('test')) return '🧪 Тест';
    if (cat) return cat;
    return '-';
  };

  const getDetails = (tx) => {
    const giftMatch = tx.description?.match(/#(\d+)/);
    if (giftMatch) return '#' + giftMatch[1];
    if (tx.description && tx.description.length > 20) {
      return tx.description.substring(0, 20) + '...';
    }
    return tx.description || '-';
  };

  return (
    <div className="admin-tx">
      <div className="admin-page-header">
        <h1 className="admin-page-title">💰 Транзакции</h1>
        <div className="admin-page-actions">
          <button className="admin-btn admin-btn-primary" onClick={fetchTransactions}>🔄</button>
        </div>
      </div>

      <div className="tx-stats">
        <div className="tx-stat">
          <span className="tx-stat-value">{stats.total || 0}</span>
          <span className="tx-stat-label">Всего</span>
        </div>
        <div className="tx-stat green">
          <span className="tx-stat-value">{stats.completed || 0}</span>
          <span className="tx-stat-label">Завершено</span>
        </div>
        <div className="tx-stat purple">
          <span className="tx-stat-value">{Math.round(stats.totalVolume || 0)} DAR</span>
          <span className="tx-stat-label">Объём</span>
        </div>
      </div>

      <div className="tx-search-wrap">
        <input
          type="text"
          className="tx-search-input"
          placeholder="🔍 Поиск по email, описанию..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">Загрузка...</div>
        ) : filteredTransactions.length === 0 ? (
          <div className="admin-empty">📭 Не найдено</div>
        ) : (
          <div className="tx-table-wrap">
            <table className="admin-table tx-table-full">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Тип</th>
                  <th>Кому</th>
                  <th>От кого</th>
                  <th>Детали</th>
                  <th>Категория</th>
                  <th>Сумма</th>
                  <th>Дата</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map(tx => {
                  const typeInfo = getTypeInfo(tx.type);
                  return (
                    <tr key={tx.id}>
                      <td className="tx-id">{tx.id}</td>
                      <td>
                        <span className={'tx-badge ' + typeInfo.color}>
                          {typeInfo.icon} {typeInfo.label}
                        </span>
                      </td>
                      <td className="tx-cell">{shortEmail(tx.to_user_email || tx.user_email)}</td>
                      <td className="tx-cell">{shortEmail(tx.from_user_email)}</td>
                      <td className="tx-cell">{getDetails(tx)}</td>
                      <td className="tx-cell-cat">{getGiftType(tx)}</td>
                      <td className="tx-amount">{parseFloat(tx.amount || 0).toFixed(2)}</td>
                      <td className="tx-date">{formatDate(tx.created_at)}</td>
                      <td>
                        <button className="tx-del" onClick={() => handleDelete(tx.id)} disabled={deleting === tx.id}>
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="tx-footer">Показано: {filteredTransactions.length}</div>
      </div>
    </div>
  );
};

export default AdminTransactions;